import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '../services/api';
import './TemplateEditorPage.css';

const MERGE_FIELDS = ['{{client_name}}', '{{company}}', '{{date}}', '{{amount}}'];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export default function TemplateEditorPage() {
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const navigate = useNavigate();

  const [title, setTitle]         = useState('');
  const [body, setBody]           = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/contract-templates/${id}`).then(t => {
      setTitle(t.title);
      setBody(t.body);
    });
  }, [id, isEdit]);

  function insertMergeField(field) {
    setBody(b => b + field);
  }

  async function handleSave() {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!body.trim())  { setError('Body is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await api.put(`/contract-templates/${id}`, { title, body });
      } else {
        await api.post('/contract-templates', { title, body });
      }
      navigate('/contracts/templates');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="template-editor">
      <div className="editor-header">
        <div>
          <button className="back-link" onClick={() => navigate('/contracts/templates')}>← Templates</button>
          <h2>{isEdit ? 'Edit Template' : 'New Template'}</h2>
        </div>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Template'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="title-field">
        <label>Template Name</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Standard Service Agreement"
        />
      </div>

      <div className="merge-bar">
        <span className="merge-label">Insert merge field:</span>
        {MERGE_FIELDS.map(f => (
          <button key={f} className="merge-btn" onClick={() => insertMergeField(f)}>{f}</button>
        ))}
      </div>

      <div className="quill-wrapper">
        <ReactQuill
          theme="snow"
          value={body}
          onChange={setBody}
          modules={QUILL_MODULES}
          placeholder="Write your contract here. Use merge fields above to insert client-specific data."
        />
      </div>
    </div>
  );
}
